import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useEventStore } from '@/store'
import { useI18n } from '@/lib/i18n'
import { useCamera } from '@/hooks/useCamera'
import { useAudio } from '@/hooks/useAudio'
import HypeOverlay from './HypeOverlay'
import { generateId, dataURLToBlob, extensionFromDataUrl, mimeFromDataUrl, compressDataUrlForUpload, saveHQPhotoLocally, toLosslessPng, fetchWithRetry, isElectronClient } from '@/lib/utils'
import { applyEffects } from '@/lib/imageEffects'
import { generateLayoutImage } from '@/lib/layoutRenderer'
import type { CaptureMode, EventConfig } from '@/types'
import { motion, AnimatePresence } from 'framer-motion'
import { X, QrCode, ArrowUpLeft, Ban, Image as ImageIcon } from 'lucide-react'
import QuickSettings from '@/booth/QuickSettings'
import { mergeOverrides } from '@/lib/boothOverrides'
import type { BoothOverrides } from '@/lib/boothOverrides'
import QRCode from 'qrcode'
import OfflineOverlay from '@/components/OfflineOverlay'
import WifiPanel from '@/components/WifiPanel'

export default function BoothApp() {
  const { eventId } = useParams<{ eventId: string }>()
  const events = useEventStore((s) => s.events)
  // Prefer the identity the Electron main process owns: it is per-machine and
  // stable across server changes, and the device agent reports under the same
  // id, so the monitor can line a booth page up with the machine running it.
  // The localStorage id stays as the fallback for a plain browser booth.
  const [deviceId] = useState(() => {
    const fromAgent = window.electronAPI?.deviceId
    if (fromAgent) return fromAgent
    let id = localStorage.getItem('booth_device_id')
    if (!id) {
      id = generateId()
      localStorage.setItem('booth_device_id', id)
    }
    return id
  })
  const [appVersion, setAppVersion] = useState<string | null>(null)
  // Shown to guests whenever a remote operator pulls a camera frame. The booth
  // camera points at people who never agreed to be watched from elsewhere, so
  // remote viewing is deliberately never silent.
  const [remoteViewActive, setRemoteViewActive] = useState(false)
  const remoteViewTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const micAnalyser = useRef<AnalyserNode | null>(null)
  const [event, setEvent] = useState<EventConfig | null>(null)
  const [screen, setScreen] = useState<'welcome' | 'mode' | 'countdown' | 'capture' | 'intermediate_review' | 'review' | 'print' | 'survey' | 'share' | 'thanks' | 'attract'>('welcome')
  const [allPoses, setAllPoses] = useState<string[]>([])
  const [poseMode, setPoseMode] = useState<'random' | 'ordered'>('random')
  const [isPreCountdown, setIsPreCountdown] = useState(false)
  const [mode, setMode] = useState<CaptureMode>('photo')
  const [photos, setPhotos] = useState<string[]>([])
  const [lastPhoto, setLastPhoto] = useState<string | null>(null)
  const [currentPose, setCurrentPose] = useState(1)
  const [countdown, setCountdown] = useState(3)
  const [sessionId, setSessionId] = useState('')
  const [overrides, setOverrides] = useState<BoothOverrides>({})
  const [showQuickSettings, setShowQuickSettings] = useState(false)
  const [galleryQrData, setGalleryQrData] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [savingStage, setSavingStage] = useState('Preparing your image...')
  const [saveError, setSaveError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [isMediaPipeLoaded, setIsMediaPipeLoaded] = useState(false)
  const [propsList, setPropsList] = useState<string[]>([])
  const [propMenuOpen, setPropMenuOpen] = useState(false)
  const [showWifiPanel, setShowWifiPanel] = useState(false)
  const [tapFeedback, setTapFeedback] = useState(0) // visual tap counter 1-4
  const [showPinGate, setShowPinGate] = useState(false)
  const [pinInput, setPinInput] = useState('')
  const [pinError, setPinError] = useState(false)
  // 4-tap bottom-left counter for settings access
  const bottomLeftTapCountRef = useRef(0)
  const topLeftTapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tapFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)


  useEffect(() => {
    const ft = event?.effects?.faceTracking
    if (ft) {
      setOverrides(prev => ({
        ...prev,
        advancedBeautyFilter: ft.enabled,
        faceTrackingProp: ft.defaultProp
      }))
    }
  }, [event])
  const beautyCanvasRef = useRef<HTMLCanvasElement>(null)
  const faceLandmarkerRef = useRef<any>(null)
  const glassesImgRef = useRef<HTMLImageElement | null>(null)
  const tapCountRef = useRef(0)
  const controlWs = useRef<WebSocket | null>(null)
  const tapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const captureRetryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const introTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const captureDelayTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const nextPoseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const zigbeeOffTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Merge operator overrides on top of the persisted event config (non-destructive)
  const effectiveEvent = useMemo(() => {
    if (!event) return null
    return mergeOverrides(event, overrides)
  }, [event, overrides])

  const effectiveEventRef = useRef(effectiveEvent)
  useEffect(() => { effectiveEventRef.current = effectiveEvent }, [effectiveEvent])

  const totalPoses = useMemo(() => {
    return effectiveEvent?.printLayout?.slots?.length || effectiveEvent?.capture.photo.poseCount || 1
  }, [effectiveEvent])

  const { videoRef, captureVideoRef, start, stop, capture, attach, startRecording, stopRecording, requestVideoBlob, isWaitingForPermission } = useCamera({
    deviceId: effectiveEvent?.camera.deviceId,
    width: effectiveEvent?.camera.resolution.width,
    height: effectiveEvent?.camera.resolution.height,
    frameRate: effectiveEvent?.camera.frameRate ?? 30,
  })
  const { playBeep, play } = useAudio()

  // Client version, reported alongside booth status so the monitor can show
  // which build each kiosk is actually running.
  useEffect(() => {
    window.electronAPI?.getAppVersion?.()
      .then((v) => setAppVersion(v))
      .catch(() => setAppVersion(null))
  }, [])

  /**
   * Ops the device agent delegates to the renderer.
   *
   * Camera and microphone only exist here — the Electron main process has no
   * getUserMedia — so the agent forwards these and we answer over IPC. Failing
   * loudly matters: "no booth page open" is a normal state and the operator
   * needs to see that rather than a hang.
   */
  useEffect(() => {
    const api = window.electronAPI
    if (!api?.onAgentRequest || !api.respondToAgent) return
    // Narrowed into locals so the async callback below doesn't re-widen them
    // back to optional.
    const respond = api.respondToAgent

    const readMicLevel = (): number | null => {
      try {
        const stream = (videoRef.current?.srcObject as MediaStream | null) || null
        if (!stream || stream.getAudioTracks().length === 0) return null
        if (!micAnalyser.current) {
          const Ctx = window.AudioContext || (window as any).webkitAudioContext
          const ctx = new Ctx()
          const source = ctx.createMediaStreamSource(stream)
          const analyser = ctx.createAnalyser()
          analyser.fftSize = 512
          source.connect(analyser)
          micAnalyser.current = analyser
        }
        const analyser = micAnalyser.current!
        const buf = new Uint8Array(analyser.frequencyBinCount)
        analyser.getByteTimeDomainData(buf)
        // RMS around the 128 midpoint, scaled to a 0-100 meter.
        let sum = 0
        for (const v of buf) sum += (v - 128) ** 2
        return Math.min(100, Math.round(Math.sqrt(sum / buf.length) * (100 / 64)))
      } catch {
        return null
      }
    }

    const snapshot = (params: { maxWidth?: number; quality?: number } = {}) => {
      const video = videoRef.current
      if (!video || !video.videoWidth) {
        throw new Error('Camera is not active on this device')
      }
      const maxWidth = params.maxWidth || 640
      const scale = Math.min(1, maxWidth / video.videoWidth)
      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale))
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale))
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Canvas unavailable')
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

      // Tell the room a frame just left the building.
      setRemoteViewActive(true)
      if (remoteViewTimer.current) clearTimeout(remoteViewTimer.current)
      remoteViewTimer.current = setTimeout(() => setRemoteViewActive(false), 4000)

      return {
        dataUrl: canvas.toDataURL('image/jpeg', params.quality ?? 0.5),
        width: canvas.width,
        height: canvas.height,
        micLevel: readMicLevel(),
        screen: screenRef.current,
        capturedAt: new Date().toISOString(),
      }
    }

    const unsubscribe = api.onAgentRequest(async ({ requestId, op, params }) => {
      try {
        if (op !== 'camera.snapshot') throw new Error(`Unknown renderer op: ${op}`)
        respond({ requestId, ok: true, data: snapshot(params as { maxWidth?: number; quality?: number }) })
      } catch (err) {
        respond({
          requestId,
          ok: false,
          error: err instanceof Error ? err.message : String(err),
        })
      }
    })

    return () => {
      unsubscribe?.()
      if (remoteViewTimer.current) clearTimeout(remoteViewTimer.current)
    }
  }, [videoRef])

  // Keep refs synced with state so timers always read latest values
  const currentPoseRef = useRef(currentPose)
  const photosRef = useRef(photos)
  const modeRef = useRef(mode)
  const eventRef = useRef(event)
  const sessionIdRef = useRef(sessionId)
  // Read by the agent snapshot handler, which is a long-lived subscription and
  // would otherwise close over a stale screen value.
  const screenRef = useRef(screen)

  useEffect(() => { currentPoseRef.current = currentPose }, [currentPose])
  useEffect(() => { photosRef.current = photos }, [photos])
  useEffect(() => { modeRef.current = mode }, [mode])
  useEffect(() => { eventRef.current = event }, [event])
  useEffect(() => { sessionIdRef.current = sessionId }, [sessionId])
  useEffect(() => { screenRef.current = screen }, [screen])

  useEffect(() => {
    if (screen === 'welcome') {
      setSessionId('')
      setPhotos([])
      setCurrentPose(1)
      setSaveError(null)
    }
  }, [screen])

  useEffect(() => {
    if (!overrides.advancedBeautyFilter || isMediaPipeLoaded) return

    const loadMediaPipe = async () => {
      try {
        // @ts-ignore
        const vision = await import('https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0')
        
        const filesetResolver = await vision.FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
        )
        
        const faceLandmarker = await vision.FaceLandmarker.createFromOptions(filesetResolver, {
          baseOptions: {
            modelAssetPath: `https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task`,
            delegate: "GPU"
          },
          runningMode: "VIDEO",
          numFaces: 4
        })
        
        faceLandmarkerRef.current = faceLandmarker
        setIsMediaPipeLoaded(true)
        console.log('MediaPipe FaceLandmarker loaded successfully')
      } catch (err) {
        console.error('Failed to load MediaPipe', err)
      }
    }

    loadMediaPipe()
  }, [overrides.advancedBeautyFilter, isMediaPipeLoaded])

  useEffect(() => {
    fetch('/api/props')
      .then(res => res.json())
      .then(data => setPropsList(data))
      .catch(err => console.error('Failed to fetch props:', err))
  }, [])

  useEffect(() => {
    const propUrl = overrides.faceTrackingProp
    if (!propUrl || propUrl === 'custom') {
      glassesImgRef.current = null
      return
    }
    
    const img = new Image()
    if (propUrl.startsWith('http')) {
      img.crossOrigin = 'anonymous'
    }
    img.src = propUrl
    img.onload = () => {
      glassesImgRef.current = img
      console.log('Prop image loaded:', propUrl)
    }
    img.onerror = () => {
      console.error('Failed to load prop image:', propUrl)
      glassesImgRef.current = null
    }
  }, [overrides.faceTrackingProp])

  useEffect(() => {
    if (!isMediaPipeLoaded || !overrides.advancedBeautyFilter) return

    const canvas = beautyCanvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animationFrameId: number

    const renderLoop = () => {
      const currentVideo = videoRef.current
      if (!currentVideo) {
        animationFrameId = requestAnimationFrame(renderLoop)
        return
      }

      if (currentVideo.readyState >= 2) {
        canvas.width = currentVideo.videoWidth
        canvas.height = currentVideo.videoHeight

        const faceLandmarker = faceLandmarkerRef.current
        if (faceLandmarker) {
          const results = faceLandmarker.detectForVideo(currentVideo, performance.now())

          // Draw original frame
          ctx.drawImage(currentVideo, 0, 0)

          if (results.faceLandmarks && results.faceLandmarks.length > 0) {
            results.faceLandmarks.forEach((landmarks: any) => {
              // Calculate face bounding box
              let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0
              landmarks.forEach((lm: any) => {
                const x = lm.x * canvas.width
                const y = lm.y * canvas.height
                if (x < minX) minX = x
                if (y < minY) minY = y
                if (x > maxX) maxX = x
                if (y > maxY) maxY = y
              })
              
              const padX = (maxX - minX) * 0.2
              const padY = (maxY - minY) * 0.2
              minX = Math.max(0, minX - padX)
              minY = Math.max(0, minY - padY)
              maxX = Math.min(canvas.width, maxX + padX)
              maxY = Math.min(canvas.height, maxY + padY)
              
              const w = maxX - minX
              const h = maxY - minY

              // Draw glasses if loaded
              const glassesImg = glassesImgRef.current
              if (glassesImg) {
                const glassesW = w * 0.8
                const glassesH = glassesW * (glassesImg.height / glassesImg.width)
                const glassesX = minX + (w - glassesW) / 2
                const glassesY = minY + h * 0.38 - glassesH / 2
                
                ctx.drawImage(glassesImg, glassesX, glassesY, glassesW, glassesH)
              }
            })
          }
        }
      }
      animationFrameId = requestAnimationFrame(renderLoop)
    }

    renderLoop()

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [isMediaPipeLoaded, overrides.advancedBeautyFilter, overrides.beautyFilterStrength])

  useEffect(() => {
    const cached = events.find((e) => e.id === eventId)
    if (cached) {
      setEvent(cached)
      return
    }
    if (!eventId) return

    // Fetch only the event this booth is actually running. This used to list
    // every event and then fetch each one's full config just to pick one —
    // N+1 requests over a weak link before the booth could even start, and it
    // silently hung on "Loading..." forever if any of them failed.
    let cancelled = false
    fetchWithRetry(`/api/events/${eventId}`, {}, { retries: 5 })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (cancelled) return
        setEvent(data.config)
      })
      .catch((err) => {
        console.error('Failed to load event config:', err)
        if (!cancelled) setLoadError(true)
      })

    return () => {
      cancelled = true
    }
  }, [events, eventId])

  const cleanupTimers = () => {
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current as unknown as any)
      countdownTimer.current = null
    }
    if (introTimer.current) {
      clearTimeout(introTimer.current)
      introTimer.current = null
    }
    if (idleTimer.current) clearTimeout(idleTimer.current)
    if (captureRetryTimer.current) clearTimeout(captureRetryTimer.current)
    // These three used to be bare setTimeout calls with no handle, so cancelling
    // a session (X button / Escape) left them running: the booth would return to
    // the welcome screen and then spontaneously jump back into a countdown or
    // fire a capture seconds later, in front of the next guest.
    if (captureDelayTimer.current) {
      clearTimeout(captureDelayTimer.current)
      captureDelayTimer.current = null
    }
    if (nextPoseTimer.current) {
      clearTimeout(nextPoseTimer.current)
      nextPoseTimer.current = null
    }
    if (zigbeeOffTimer.current) {
      clearTimeout(zigbeeOffTimer.current)
      zigbeeOffTimer.current = null
    }
  }

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && screen !== 'welcome') {
        cleanupTimers()
        setScreen('welcome')
        setPhotos([])
        setCurrentPose(1)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [screen])

  useEffect(() => {
    if (!eventId) return
    fetch(`/api/poses?eventId=${eventId}`)
      .then((r) => r.json())
      .then((data) => setAllPoses(data))
      .catch(() => {})
  }, [eventId])

  useEffect(() => {
    if (effectiveEvent?.capture.poseSuggestions?.mode) {
      setPoseMode(effectiveEvent.capture.poseSuggestions.mode)
    }
  }, [effectiveEvent])

  useEffect(() => {
    if (!eventId) return

    // Auto-reconnecting control socket. Previously a single dropped connection
    // (routine on a 4G hotspot) silently killed remote control and pose-rigging
    // for the rest of the event, with no indication anything was wrong.
    let ws: WebSocket | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let attempt = 0
    let closedByUs = false

    const connect = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      ws = new WebSocket(`${protocol}//${window.location.host}?eventId=${eventId}`)
      controlWs.current = ws

      ws.onopen = () => {
        attempt = 0
      }

      ws.onmessage = async (e) => {
        try {
          let text = ''
          if (e.data instanceof Blob) {
            text = await e.data.text()
          } else {
            text = e.data
          }
          const message = JSON.parse(text)
          if (message.type === 'START_COUNTDOWN') {
            beginCountdown(message.mode || 'photo')
          } else if (message.type === 'STOP_COUNTDOWN') {
            setScreen('welcome')
            cleanupTimers()
          } else if (message.type === 'UPDATE_SETTINGS') {
            setOverrides((prev) => ({ ...prev, ...message.settings }))
          } else if (message.type === 'TRIGGER_ZIGBEE_LIGHT') {
            setZigbeeLight(message.state)
          } else if (message.type === 'RELOAD_KIOSK') {
            if (!message.target || message.target === deviceId) {
              window.location.reload()
            }
          } else if (message.type === 'RESTART_EVENT') {
            if (!message.target || message.target === deviceId) {
              setScreen('welcome')
              cleanupTimers()
            }
          } else if (message.type === 'EXIT_FULLSCREEN') {
            if (document.fullscreenElement) {
              document.exitFullscreen().catch(err => console.error(err))
            }
          }
        } catch (err) {
          console.error('Failed to parse WS message in booth', err)
        }
      }

      // Errors surface as a close too; let onclose own the retry scheduling.
      ws.onerror = () => ws?.close()

      ws.onclose = () => {
        if (closedByUs) return
        // 1s, 2s, 4s … capped at 30s so it keeps trying all night without
        // hammering the server when the venue link is down for a long stretch.
        const delay = Math.min(1000 * 2 ** attempt, 30000)
        attempt += 1
        console.warn(`Booth control socket lost — reconnecting in ${delay}ms`)
        reconnectTimer = setTimeout(connect, delay)
      }
    }

    connect()

    return () => {
      closedByUs = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      ws?.close()
    }
  }, [eventId, deviceId])

  // Broadcast booth status periodically and on state changes
  useEffect(() => {
    if (!eventId || !deviceId) return
    const broadcastStatus = () => {
      if (controlWs.current?.readyState === WebSocket.OPEN) {
        controlWs.current.send(JSON.stringify({
          type: 'BOOTH_STATUS',
          deviceId,
          screen,
          currentPose,
          mode,
          // So the monitor can show which build each kiosk is running. Null in a
          // plain browser booth, and on Electron clients predating the agent.
          appVersion,
          timestamp: Date.now()
        }))
      }
    }
    // Broadcast immediately on change
    broadcastStatus()
    
    // And periodically as a heartbeat
    const interval = setInterval(broadcastStatus, 5000)
    return () => clearInterval(interval)
    // appVersion resolves asynchronously after mount; without it here the
    // interval would keep broadcasting the null it captured on first run.
  }, [eventId, deviceId, screen, currentPose, mode, appVersion])

  // Auto-fullscreen on first click
  useEffect(() => {
    const handleFirstClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(err => {
          console.log('Failed to auto-fullscreen:', err)
        })
      }
      window.removeEventListener('click', handleFirstClick)
    }
    window.addEventListener('click', handleFirstClick)
    return () => window.removeEventListener('click', handleFirstClick)
  }, [])

  // Zigbee2MQTT Light Control
  const zigbeeWs = useRef<WebSocket | null>(null)
  
  useEffect(() => {
    const ev = event
    const zigbeeConfig = ev?.capture?.zigbee
    
    const enabled = overrides.zigbeeLightEnabled !== undefined 
      ? overrides.zigbeeLightEnabled 
      : (zigbeeConfig?.enabled ?? false)
      
    if (!enabled) {
      if (zigbeeWs.current) {
        zigbeeWs.current.close()
        zigbeeWs.current = null
      }
      return
    }
    
    const wsUrl = overrides.zigbeeWsUrl || zigbeeConfig?.wsUrl || 'ws://localhost:8081/api'
    console.log('Connecting to Zigbee2MQTT:', wsUrl)
    const ws = new WebSocket(wsUrl)
    
    ws.onopen = () => {
      console.log('Connected to Zigbee2MQTT WebSocket')
    }
    
    ws.onerror = (err) => {
      console.error('Zigbee2MQTT WebSocket error:', err)
    }
    
    ws.onclose = () => {
      console.log('Zigbee2MQTT WebSocket closed')
      zigbeeWs.current = null
    }
    
    zigbeeWs.current = ws
    
    return () => {
      ws.close()
    }
  }, [overrides.zigbeeLightEnabled, overrides.zigbeeWsUrl, event])

  const setZigbeeLight = (state: 'ON' | 'OFF') => {
    const ws = zigbeeWs.current
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn('Zigbee2MQTT WebSocket not connected')
      return
    }
    
    const ev = event
    const device = overrides.zigbeeLightDevice || ev?.capture?.zigbee?.device || 'spotlight'
    const message = {
      topic: `${device}/set/state`,
      payload: state
    }
    
    ws.send(JSON.stringify(message))
    console.log(`Sent Zigbee light command: ${state}`)
  }

  useEffect(() => {
    const ev = event
    const zigbeeConfig = ev?.capture?.zigbee
    const mode = zigbeeConfig?.mode || 'auto'
    
    if (mode !== 'session') return
    
    const enabled = overrides.zigbeeLightEnabled !== undefined 
      ? overrides.zigbeeLightEnabled 
      : (zigbeeConfig?.enabled ?? false)
      
    if (!enabled) return
    
    const isIdle = screen === 'welcome' || screen === 'attract'
    
    if (!isIdle) {
      setZigbeeLight('ON')
    } else {
      setZigbeeLight('OFF')
    }
  }, [screen, event, overrides.zigbeeLightEnabled])

  useEffect(() => {
    if (event && screen !== 'attract') {
      resetIdle()
    }
  }, [screen, event])

  // Ensure camera is active whenever we need it
  // True while a separate motion camera is live, so the photobooth camera can rest
  const [motionCameraActive, setMotionCameraActive] = useState(false)
  const pauseBoothCamera = motionCameraActive && screen === 'attract'

  useEffect(() => {
    if (!event) return
    if (pauseBoothCamera) {
      // Screensaver is up and another camera watches for guests: release the
      // photobooth (USB) camera to save wear and heat. It restarts on wake.
      stop()
      return
    }
    // Otherwise start once and keep it active to avoid stream interruptions
    start()
  }, [event, start, stop, pauseBoothCamera])

  // Generate gallery QR code when event loads
  useEffect(() => {
    if (event) {
      const url = `${window.location.origin}/gallery?eventId=${event.id}&public=true`
      QRCode.toDataURL(url, { margin: 1, width: 200 }).then(setGalleryQrData)
    }
  }, [event])

  useEffect(() => {
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current)
      if (captureRetryTimer.current) clearTimeout(captureRetryTimer.current)
    }
  }, [stop])

  // Motion detection for Attract Loop
  useEffect(() => {
    if (effectiveEvent?.capture.attractLoop?.wakeMethod !== 'motion') return
    if (screen !== 'welcome' && screen !== 'attract') return

    // A dedicated low-res stream when a separate motion camera is chosen;
    // otherwise reuse the photobooth camera's video element.
    const motionDeviceId = effectiveEvent?.capture.attractLoop?.motionCameraDeviceId
    const useSeparate = !!motionDeviceId && motionDeviceId !== effectiveEvent?.camera.deviceId
    let video: HTMLVideoElement | null = captureVideoRef.current
    let motionStream: MediaStream | null = null
    let motionVideo: HTMLVideoElement | null = null
    let cancelled = false
    let intervalId: ReturnType<typeof setInterval> | undefined

    const canvas = document.createElement('canvas')
    canvas.width = 32
    canvas.height = 32
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return

    let prevData: Uint8ClampedArray | null = null

    const checkMotion = () => {
      if (video && video.readyState >= 2) { // HAVE_CURRENT_DATA
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        const currentData = ctx.getImageData(0, 0, canvas.width, canvas.height).data

        if (prevData) {
          let diff = 0
          for (let i = 0; i < currentData.length; i += 4) {
            diff += Math.abs(currentData[i] - prevData[i])
            diff += Math.abs(currentData[i+1] - prevData[i+1])
            diff += Math.abs(currentData[i+2] - prevData[i+2])
          }

          const sensitivity = effectiveEvent?.capture.attractLoop?.motionSensitivity || 50
          // Map 1-100 to 150,000-5,000 threshold
          const threshold = Math.max(5000, 150000 - (sensitivity * 1400))
          
          if (diff > threshold) {
            console.log('Motion detected!', diff)
            resetIdle() // Reset idle timer to keep awake
            if (screen === 'attract') {
              setScreen('welcome')
              return true // Stop interval because we are changing screen
            }
          }
        }
        prevData = currentData
      }
      return false
    }

    const startPolling = () => {
      intervalId = setInterval(() => {
        const stopped = checkMotion()
        if (stopped) clearInterval(intervalId)
      }, 200) // Check every 200ms instead of every frame
    }

    if (useSeparate) {
      video = null
      navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: motionDeviceId }, width: { ideal: 160 }, height: { ideal: 120 }, frameRate: { ideal: 10 } },
        audio: false,
      }).then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return }
        motionStream = stream
        motionVideo = document.createElement('video')
        motionVideo.muted = true
        motionVideo.playsInline = true
        motionVideo.srcObject = stream
        motionVideo.play().catch(() => {})
        video = motionVideo
        setMotionCameraActive(true)
        startPolling()
      }).catch((err) => {
        // Camera missing on this machine: fall back to the photobooth camera
        console.warn('Motion camera unavailable, using photobooth camera', err)
        if (cancelled) return
        video = captureVideoRef.current
        startPolling()
      })
    } else {
      if (!video) return
      startPolling()
    }

    return () => {
      cancelled = true
      if (intervalId) clearInterval(intervalId)
      if (useSeparate) setMotionCameraActive(false)
      motionStream?.getTracks().forEach((t) => t.stop())
      if (motionVideo) motionVideo.srcObject = null
    }
  }, [screen, effectiveEvent?.capture.attractLoop?.wakeMethod, effectiveEvent?.capture.attractLoop?.motionCameraDeviceId, effectiveEvent?.camera.deviceId])

  const resetIdle = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current)
    const timeout = (screen === 'welcome' && effectiveEvent?.capture.attractLoop?.enabled)
      ? (effectiveEvent.capture.attractLoop.timeout * 1000) 
      : 60000
    
    idleTimer.current = setTimeout(() => {
      if (effectiveEvent?.capture.attractLoop?.enabled && screen === 'welcome') {
        setScreen('attract')
      } else {
        setScreen('welcome')
        setPhotos([])
        setCurrentPose(1)
      }
    }, timeout)
  }

  const handleStart = () => {
    const ev = eventRef.current
    if (!ev) return
    if (ev.capture.enabledModes.length === 1) {
      setMode(ev.capture.enabledModes[0])
      beginCountdown(ev.capture.enabledModes[0])
    } else {
      setScreen('mode')
    }
  }

  const beginCountdown = (selectedMode: CaptureMode, isFirst = true) => {
    const ev = effectiveEventRef.current
    if (!ev) return
    
    if (isFirst && eventId) {
      fetch(`/api/poses?eventId=${eventId}`)
        .then((r) => r.json())
        .then((data) => setAllPoses(data))
        .catch(() => {})
    }
    
    setMode(selectedMode)
    if (isFirst) {
      setPhotos([])
      setCurrentPose(1)
      currentPoseRef.current = 1
      if (ev.capture.recordSessionVideo) {
        startRecording()
      }
    }
    const delay = isFirst ? (selectedMode === 'photo' ? ev.capture.photo.delayBeforeFirst : 3) : (ev.capture.photo.delayBetweenPoses || 2)
    setCountdown(delay)
    
    const introDuration = (ev.capture.poseSuggestions?.enabled ?? true) ? (ev.capture.poseSuggestions?.showSecondsBefore ?? 2) : 0
    setIsPreCountdown(introDuration > 0)
    setScreen('countdown')

    cleanupTimers()
    introTimer.current = setTimeout(() => {
      setIsPreCountdown(false)
      let cd = delay
      
      // Turn on Zigbee light if enabled (during countdown!)
      const ev = event
      const zigbeeConfig = ev?.capture?.zigbee
      const mode = zigbeeConfig?.mode || 'auto'
      const enabled = overrides.zigbeeLightEnabled !== undefined 
        ? overrides.zigbeeLightEnabled 
        : (mode !== 'off' && (zigbeeConfig?.enabled ?? false))
        
      if (enabled && mode === 'auto') {
        setZigbeeLight('ON')
      }

      countdownTimer.current = setInterval(() => {
        cd -= 1
        setCountdown(cd)
        if (ev?.audio?.counterSoundEnabled ?? true) {
          playBeep()
        }
        if (cd <= 0) {
          cleanupTimers()
          setScreen('capture')
          
          // Give camera time to attach to the new video element
          captureDelayTimer.current = setTimeout(() => takePhoto(selectedMode), 800)

          // Turn off Zigbee light after photo is taken
          if (enabled && mode === 'auto') {
            const offDelay = zigbeeConfig?.offAfterSeconds !== undefined ? zigbeeConfig.offAfterSeconds * 1000 : 2000
            zigbeeOffTimer.current = setTimeout(() => {
              setZigbeeLight('OFF')
            }, offDelay)
          }
        }
      }, 1000) as unknown as ReturnType<typeof setTimeout>
    }, introDuration * 1000)
  }

  const takePhoto = async (selectedMode: CaptureMode, retries = 15) => {
    const ev = effectiveEventRef.current
    if (!ev) return
    let dataUrl
    if (overrides.advancedBeautyFilter && beautyCanvasRef.current) {
      dataUrl = beautyCanvasRef.current.toDataURL('image/jpeg', 0.95)
    } else {
      dataUrl = capture()
    }
    if (!dataUrl && retries > 0) {
      captureRetryTimer.current = setTimeout(() => takePhoto(selectedMode, retries - 1), 200)
      return
    }
    if (!dataUrl) {
      // Camera never produced a frame. If we have nothing at all, don't fall
      // through to review/save — that created an empty session with no photos.
      // Reset to welcome so the booth is immediately usable by the next guest.
      if (photosRef.current.length === 0) {
        console.error('Capture failed with no usable frames — resetting booth')
        if (ev.capture.recordSessionVideo) {
          await stopRecording().catch(() => {})
        }
        cleanupTimers()
        setScreen('welcome')
        return
      }
      // Otherwise keep whatever poses we did get.
      setScreen('review')
      return
    }

    if (ev.audio?.shutterSoundEnabled ?? true) {
      play('/sounds/flash_sound.mp3').catch(err => console.error('Failed to play shutter sound', err))
    }

    try {
      // Apply effects immediately so they are visible in review screen
      dataUrl = await applyEffects(dataUrl, ev.effects)
    } catch (err) {
      console.error('Failed to apply effects', err)
    }

    const newPhotos = [...photosRef.current, dataUrl]
    setPhotos(newPhotos)
    photosRef.current = newPhotos
    setLastPhoto(dataUrl)

    if (selectedMode === 'photo') {
      const total = ev.printLayout?.slots?.length || ev.capture.photo.poseCount || 1
      const pose = currentPoseRef.current
      if (pose < total) {
        const reviewTime = ev.capture.photo.reviewTime || 2
        setScreen('intermediate_review')

        nextPoseTimer.current = setTimeout(() => {
          const nextPose = pose + 1
          setCurrentPose(nextPose)
          currentPoseRef.current = nextPose
          beginCountdown(selectedMode, false)
        }, reviewTime * 1000)
      } else {
        if (ev.capture.recordSessionVideo) {
          await stopRecording()
        }
        await handleAccept()
      }
    } else {
      if (ev.capture.recordSessionVideo) {
        await stopRecording()
      }
      await handleAccept()
    }
  }

  const savePhotos = async () => {
    const ev = eventRef.current
    const sid = sessionIdRef.current || generateId()
    // Surfaced to the guest while a retry is in flight, so a slow link looks
    // like progress rather than a frozen booth.
    const notifyRetry = (attempt: number, total: number) =>
      setSavingStage(`Connection is slow — retrying (${attempt}/${total})…`)

    if (!sessionIdRef.current) {
      setSessionId(sid)
      sessionIdRef.current = sid
      const res = await fetchWithRetry(
        '/api/sessions',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: sid, eventId: ev?.id }),
        },
        { onRetry: notifyRetry }
      )
      if (!res.ok) throw new Error('Failed to create session')
    }
    const dataSaver = ev?.capture.dataSaver
    setSavingStage(dataSaver?.enabled ? 'Compressing your photos...' : 'Uploading your photos...')
    for (let i = 0; i < photosRef.current.length; i++) {
      const photoData = photosRef.current[i]
      const photoId = generateId()
      // Archive a genuinely lossless copy to disk when running in the Electron
      // kiosk client — independent of whatever quality gets uploaded below.
      // (photoData itself is JPEG q0.92 — high quality, but still lossy, so
      // re-encode as PNG rather than saving those bytes directly.)
      // Guarded on isElectronClient(): in a plain browser this whole encode is
      // discarded, and it's a multi-MB main-thread canvas operation per photo.
      if (isElectronClient()) {
        toLosslessPng(photoData).then((hqData) =>
          saveHQPhotoLocally(ev?.name || '', sid, `photo-${photoId}.png`, hqData)
        )
      }
      // The in-memory photosRef copy stays full quality for printing regardless —
      // only the copy sent over the network gets shrunk when Data Saver is on.
      const uploadData = dataSaver?.enabled
        ? await compressDataUrlForUpload(photoData, dataSaver.quality, dataSaver.maxDimension)
        : photoData
      const blob = dataURLToBlob(uploadData)
      const filename = `photo-${photoId}.${extensionFromDataUrl(uploadData)}`
      const captureRes = await fetchWithRetry(
        `/api/captures/${ev?.id}/${sid}?filename=${filename}`,
        {
          method: 'POST',
          headers: { 'Content-Type': mimeFromDataUrl(uploadData) },
          body: blob,
        },
        { timeoutMs: 45000, onRetry: notifyRetry }
      )
      if (!captureRes.ok) throw new Error('Failed to upload photo')

      const recordRes = await fetchWithRetry(
        '/api/sessions/' + sid + '/photos',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: photoId, type: 'photo', filename }),
        },
        { onRetry: notifyRetry }
      )
      if (!recordRes.ok) throw new Error('Failed to record photo in database')
      setSavingStage(dataSaver?.enabled ? 'Compressing your photos...' : 'Uploading your photos...')
    }
 
    if (ev?.capture.recordSessionVideo) {
      const videoBlob = requestVideoBlob()
      if (videoBlob) {
        const mimeType = videoBlob.type || 'video/webm'
        const extension = mimeType.includes('mp4') ? 'mp4' : 'webm'
        const videoId = generateId()
        const videoFilename = `video-${videoId}.${extension}`
        // Local archive always happens, independent of network conditions.
        saveHQPhotoLocally(ev.name, sid, videoFilename, videoBlob)

        // Video is by far the largest single asset in a session — skip the
        // network upload entirely when Data Saver is on. The local copy above
        // is saved either way, so nothing is lost, just not sent over a weak connection.
        if (!dataSaver?.enabled) {
          setSavingStage('Uploading your video...')
          const formData = new FormData()
          formData.append('file', videoBlob, videoFilename)

          // Videos are ~16-18MB; give them a much longer ceiling than photos.
          const videoUploadRes = await fetchWithRetry(
            `/api/captures/${ev.id}/${sid}/upload?filename=${videoFilename}`,
            { method: 'POST', body: formData },
            { timeoutMs: 180000, retries: 2, onRetry: notifyRetry }
          )
          if (!videoUploadRes.ok) throw new Error('Failed to upload video')

          const videoRecordRes = await fetchWithRetry(
            '/api/sessions/' + sid + '/photos',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: videoId, type: 'video', filename: videoFilename }),
            },
            { onRetry: notifyRetry }
          )
          if (!videoRecordRes.ok) throw new Error('Failed to record video in database')
        }
      }
    }

    // Generate and save the composed print layout
    if (ev) {
      try {
        setSavingStage('Preparing your print...')
        const layoutDataUrl = await generateLayoutImage(ev, photosRef.current)
        if (layoutDataUrl) {
          const layoutId = generateId()
          if (isElectronClient()) {
            toLosslessPng(layoutDataUrl).then((hqLayoutData) =>
              saveHQPhotoLocally(ev.name, sid, `layout-${layoutId}.png`, hqLayoutData)
            )
          }
          const uploadLayoutData = dataSaver?.enabled
            ? await compressDataUrlForUpload(layoutDataUrl, dataSaver.quality, dataSaver.maxDimension)
            : layoutDataUrl
          const blob = dataURLToBlob(uploadLayoutData)
          const filename = `layout-${layoutId}.${extensionFromDataUrl(uploadLayoutData)}`
          setSavingStage('Uploading your print...')
          const layoutUploadRes = await fetchWithRetry(
            `/api/captures/${ev.id}/${sid}?filename=${filename}`,
            {
              method: 'POST',
              headers: { 'Content-Type': mimeFromDataUrl(uploadLayoutData) },
              body: blob,
            },
            { timeoutMs: 45000, onRetry: notifyRetry }
          )
          if (!layoutUploadRes.ok) throw new Error('Failed to upload layout image')

          const layoutRecordRes = await fetchWithRetry(
            '/api/sessions/' + sid + '/photos',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: layoutId, type: 'layout', filename }),
            },
            { onRetry: notifyRetry }
          )
          if (!layoutRecordRes.ok) throw new Error('Failed to record layout in database')
        }
      } catch (err) {
        console.error('Failed to save layout:', err)
      }
    }
  }

  const handleAccept = async () => {
    setSavingStage('Preparing your image...')
    setSaveError(null)
    setIsSaving(true)
    try {
      await savePhotos()
      setOverrides({ ...overrides, advancedBeautyFilter: false })
      const ev = eventRef.current
      if (ev?.survey && ev.survey.length > 0) {
        setScreen('survey')
      } else if (ev?.sharing.qrEnabled || ev?.sharing.emailEnabled) {
        setScreen('share')
      } else {
        setScreen('thanks')
      }
    } catch (err: any) {
      // Deliberately NOT alert(): a native dialog blocks the entire booth until
      // a human dismisses it, which at an unattended event means the booth is
      // dead for the rest of the night. Show a non-blocking notice instead and
      // let the idle timer return to the welcome screen on its own.
      console.error('Session save failed:', err)
      setSaveError(err?.message || 'Unknown error')
      setScreen('thanks')
    } finally {
      setIsSaving(false)
    }
  }

  const handlePrint = () => {
    setScreen('print')
  }



  if (!event || !effectiveEvent) {
    // Distinguish "still loading" from "gave up" — an unattended booth stuck on
    // a permanent "Loading..." looks identical to a hang, so give it a retry.
    if (loadError) {
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-5 bg-black px-8 text-center text-white">
          <h1 className="text-2xl font-bold uppercase tracking-widest">Can't reach the server</h1>
          <p className="max-w-md text-white/50">
            The booth couldn't load its settings. Check the network connection.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-full border border-white/15 bg-white/10 px-8 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-white/20 transition-colors"
          >
            Try again
          </button>
        </div>
      )
    }
    return <div className="flex h-screen items-center justify-center bg-black text-white">Loading...</div>
  }

  // Triple-tap the bottom-right corner of the welcome screen to open Quick Settings
  const handleCornerTap = (e: React.MouseEvent) => {
    e.stopPropagation()
    tapCountRef.current += 1
    if (tapTimerRef.current) clearTimeout(tapTimerRef.current)
    tapTimerRef.current = setTimeout(() => { tapCountRef.current = 0 }, 800)
    if (tapCountRef.current >= 3) {
      tapCountRef.current = 0
      setShowQuickSettings(true)
    }
  }

  // 4-tap bottom-left corner to open settings (any screen, always available)
  const handleBottomLeftTap = (e: React.MouseEvent) => {
    e.stopPropagation()
    bottomLeftTapCountRef.current += 1
    const count = bottomLeftTapCountRef.current
    setTapFeedback(count)
    if (topLeftTapTimerRef.current) clearTimeout(topLeftTapTimerRef.current)
    if (tapFeedbackTimerRef.current) clearTimeout(tapFeedbackTimerRef.current)
    topLeftTapTimerRef.current = setTimeout(() => {
      bottomLeftTapCountRef.current = 0
      setTapFeedback(0)
    }, 2500)
    tapFeedbackTimerRef.current = setTimeout(() => setTapFeedback(0), 800)
    if (bottomLeftTapCountRef.current >= 4) {
      bottomLeftTapCountRef.current = 0
      setTapFeedback(0)
      // Show PIN gate before allowing access
      setPinInput('')
      setPinError(false)
      setShowPinGate(true)
    }
  }


  const branding = effectiveEvent.branding

  return (
    <div
      className="relative h-screen w-screen overflow-hidden select-none"
      style={{ backgroundColor: '#000', fontFamily: branding.fontFamily }}
      onContextMenu={(e) => e.preventDefault()}
      onClick={resetIdle}
    >
      {/* Offline/maintenance overlay — sits on top of everything */}
      <OfflineOverlay />

      {/* A remote operator just pulled a camera frame. Guests in front of this
          booth never agreed to be watched from elsewhere, so this is shown
          rather than letting the view happen silently. */}
      {remoteViewActive && (
        <div className="fixed top-4 left-1/2 z-[200] -translate-x-1/2 flex items-center gap-2 rounded-full border border-red-500/40 bg-black/80 px-4 py-2 text-sm font-medium text-red-300 backdrop-blur">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
          </span>
          Camerabeeld bekeken door beheerder
        </div>
      )}

      <AnimatePresence>
        {isSaving && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-md"
          >
            <div className="flex flex-col items-center gap-6">
              <div className="relative h-24 w-24">
                <div
                  className="absolute inset-0 rounded-full blur-xl opacity-50"
                  style={{ backgroundColor: branding.primaryColor || '#ffffff' }}
                />
                <div
                  className="absolute inset-0 rounded-full animate-spin"
                  style={{
                    border: '4px solid transparent',
                    borderTopColor: branding.primaryColor || '#ffffff',
                    borderRightColor: branding.accentColor || branding.primaryColor || '#ffffff',
                  }}
                />
                <div className="absolute inset-2 rounded-full border-4 border-white/10" />
                <motion.div
                  className="absolute inset-0 flex items-center justify-center"
                  animate={{ scale: [1, 1.08, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <ImageIcon className="h-8 w-8 text-white/80" strokeWidth={1.75} />
                </motion.div>
              </div>
              <div className="flex flex-col items-center gap-1.5 text-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={savingStage}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.25 }}
                    className="text-xl font-bold text-white tracking-widest uppercase"
                  >
                    {savingStage}
                  </motion.div>
                </AnimatePresence>
                <div className="text-sm text-white/40">This won't take long</div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Non-blocking save-failure notice. Auto-clears when the booth returns
          to the welcome screen, so an unattended booth recovers by itself. */}
      <AnimatePresence>
        {saveError && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-6 left-1/2 z-[150] -translate-x-1/2 rounded-2xl border border-amber-400/30 bg-amber-500/15 px-6 py-3 backdrop-blur-md"
          >
            <div className="text-center">
              <div className="text-sm font-bold uppercase tracking-widest text-amber-200">
                Photos couldn't be uploaded
              </div>
              <div className="mt-0.5 text-xs text-amber-100/60">
                They're saved on this device — the booth is still ready for the next guest.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {screen !== 'welcome' && screen !== 'thanks' && (
        <button
          onClick={() => { cleanupTimers(); setScreen('welcome'); setPhotos([]); setCurrentPose(1); }}
          className="absolute top-8 left-8 z-[100] flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4 text-white shadow-2xl transition-all active:scale-95 shadow-red-500/50 before:absolute before:-inset-10 before:content-['']"
          title="Cancel Session"
        >
          <X size={40} strokeWidth={4} />
          <span className="text-xl font-bold tracking-wider">ANNULEREN</span>
        </button>
      )}

      {/* Triple-tap trigger zone for Quick Settings (bottom-right corner, welcome screen only) */}
      {screen === 'welcome' && (
        <button
          onClick={handleCornerTap}
          className="absolute bottom-0 right-0 w-40 h-40 z-[100] cursor-default"
          title=""
          aria-hidden
        />
      )}

      {/* 4-tap trigger zone — bottom-left corner, always on top of everything */}
      <button
        onClick={handleBottomLeftTap}
        className="absolute bottom-0 left-0 w-40 h-40 z-[9999] cursor-default"
        aria-hidden
      />

      {/* Visual tap counter feedback */}
      {tapFeedback > 0 && (
        <div className="absolute bottom-4 left-4 z-[9999] pointer-events-none">
          <div className="flex gap-1.5">
            {[1,2,3,4].map(i => (
              <div
                key={i}
                className={`h-2 w-2 rounded-full transition-all duration-150 ${
                  i <= tapFeedback ? 'bg-white/80 scale-110' : 'bg-white/20'
                }`}
              />
            ))}
          </div>
        </div>
      )}


      {/* PIN gate modal — shown after 4-tap, before settings open */}
      {showPinGate && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className={`bg-zinc-900 border rounded-2xl p-8 w-72 shadow-2xl flex flex-col items-center gap-6 transition-all duration-150 ${pinError ? 'border-red-500/80 animate-pulse' : 'border-zinc-700'}`}>
            <div className="text-white font-bold text-lg tracking-wide">Operator toegang</div>

            {/* 4 dots showing how many digits entered */}
            <div className="flex gap-3">
              {[0,1,2,3].map(i => (
                <div key={i} className={`h-4 w-4 rounded-full border-2 transition-all duration-150 ${pinInput.length > i ? (pinError ? 'bg-red-500 border-red-500' : 'bg-white border-white') : 'bg-transparent border-zinc-600'}`} />
              ))}
            </div>

            {pinError && <p className="text-red-400 text-sm -mt-2">Onjuiste PIN</p>}

            {/* Numeric keypad */}
            <div className="grid grid-cols-3 gap-3 w-full">
              {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((key, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    if (key === '⌫') {
                      setPinInput(p => p.slice(0,-1))
                    } else if (key === '') {
                      // spacer
                    } else {
                      const next = pinInput + String(key)
                      setPinInput(next)
                      if (next.length >= 4) {
                        // auto-submit when 4+ digits entered
                        const api = (window as any).electronAPI
                        api?.getSettings?.().then((s: any) => {
                          const correct = s?.pinCode || '0000'
                          if (next === correct) {
                            setShowPinGate(false)
                            setPinInput('')
                            setPinError(false)
                            if (api?.openSettings) api.openSettings()
                            else setShowWifiPanel(true)
                          } else {
                            setPinError(true)
                            setPinInput('')
                            setTimeout(() => setPinError(false), 1500)
                          }
                        }).catch(() => {
                          // If no Electron, compare against '0000'
                          if (next === '0000') {
                            setShowPinGate(false)
                            setPinInput('')
                            setShowWifiPanel(true)
                          } else {
                            setPinError(true)
                            setPinInput('')
                            setTimeout(() => setPinError(false), 1500)
                          }
                        })
                      }
                    }
                  }}
                  disabled={key === ''}
                  className={`h-14 rounded-xl text-lg font-semibold transition-all duration-100 ${
                    key === '' ? 'invisible' :
                    key === '⌫' ? 'bg-zinc-700 hover:bg-zinc-600 text-zinc-300 active:scale-95' :
                    'bg-zinc-800 hover:bg-zinc-700 text-white active:scale-95'
                  }`}
                >
                  {key}
                </button>
              ))}
            </div>

            <button
              onClick={() => { setShowPinGate(false); setPinInput(''); setPinError(false) }}
              className="text-zinc-500 text-sm hover:text-zinc-300 transition-colors"
            >
              Annuleren
            </button>
          </div>
        </div>
      )}

      {/* WiFi panel (shown on 4-tap when no Electron) */}
      {showWifiPanel && <WifiPanel onClose={() => setShowWifiPanel(false)} />}


      {overrides.advancedBeautyFilter && (
        <canvas ref={beautyCanvasRef} className="absolute inset-0 h-full w-full object-cover" style={{ zIndex: 0 }} />
      )}
      {(screen === 'welcome' || screen === 'countdown' || screen === 'intermediate_review') && (
        <>
          {/* Toggle Button */}
          <button 
            onClick={(e) => {
              e.stopPropagation()
              setPropMenuOpen(!propMenuOpen)
              if (!overrides.advancedBeautyFilter) {
                setOverrides({ ...overrides, advancedBeautyFilter: true })
              }
            }}
            className="absolute top-6 right-6 z-50 p-3 bg-black/50 rounded-full hover:bg-black/70 transition-colors border border-white/20"
          >
            <img src="/props/glasses-sunglasses-red.png" className="w-12 h-12 object-contain" alt="Props" />
          </button>

          {/* Sidebar Menu */}
          {propMenuOpen && (
            <div className="absolute top-20 right-4 z-50 grid grid-cols-2 gap-2 bg-black/60 p-3 rounded-2xl max-h-[70vh] overflow-y-auto backdrop-blur-md border border-white/10">
              {/* Disable Props Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setOverrides({ ...overrides, faceTrackingProp: undefined })
                }}
                className={`p-1.5 rounded-xl hover:bg-white/20 transition-colors ${!overrides.faceTrackingProp ? 'bg-white/30 border border-white' : 'bg-black/20'}`}
                title="Disable Props"
              >
                <div className="w-12 h-12 flex items-center justify-center">
                  <Ban className="w-8 h-8 text-white/70" />
                </div>
              </button>

              {propsList.map(prop => {
                const propPath = `/props/${prop}`
                const isActive = overrides.faceTrackingProp === propPath
                return (
                  <button
                    key={prop}
                    onClick={(e) => {
                      e.stopPropagation()
                      setOverrides({ ...overrides, faceTrackingProp: isActive ? undefined : propPath })
                    }}
                    className={`p-1.5 rounded-xl hover:bg-white/20 transition-colors ${isActive ? 'bg-white/30 border border-white' : 'bg-black/20'}`}
                  >
                    <img src={propPath} className="w-12 h-12 object-contain" alt={prop} />
                  </button>
                )
              })}
            </div>
          )}
        </>
      )}
      <AnimatePresence mode="wait">
        {screen === 'attract' && (
          <div 
            key="attract" 
            className="absolute inset-0 z-[200] bg-black"
            onClick={() => {
              setScreen('welcome')
              resetIdle()
            }}
          >

            <video
              src={effectiveEvent?.capture.attractLoop?.videoAsset || '/video/bg_video.mp4'}
              autoPlay
              loop
              muted
              className="w-full h-full object-cover"
            />

            <HypeOverlay 
              active={true} 
              phrases={effectiveEvent?.capture.attractLoop?.phrases} 
              maxMessages={effectiveEvent?.capture.attractLoop?.maxMessages}
              intervalSeconds={effectiveEvent?.capture.attractLoop?.intervalSeconds}
            />
          </div>
        )}
        {screen === 'welcome' && (
          <WelcomeScreen key="welcome" event={effectiveEvent} onStart={handleStart} videoRef={videoRef} attach={attach} galleryQrData={galleryQrData} overrides={overrides} />
        )}
        {screen === 'mode' && (
          <ModeSelectScreen key="mode" event={effectiveEvent} onSelect={beginCountdown} />
        )}
        {screen === 'countdown' && (
          <CountdownScreen 
            key="countdown" 
            videoRef={videoRef} 
            event={effectiveEvent} 
            countdown={countdown} 
            currentPose={currentPose} 
            totalPoses={totalPoses} 
            attach={attach} 
            allPoses={allPoses}
            isPreCountdown={isPreCountdown}
            overrides={overrides}
            poseMode={poseMode}
          />
        )}
        {screen === 'capture' && (
          <CaptureScreen key="capture" videoRef={videoRef} event={effectiveEvent} currentPose={currentPose} totalPoses={totalPoses} photos={photos} attach={attach} overrides={overrides} />
        )}
        {screen === 'intermediate_review' && (
          <IntermediateReviewScreen key="inter-review" photo={lastPhoto} photos={photos} currentPose={currentPose} totalPoses={totalPoses} />
        )}
        {screen === 'review' && (
          <ReviewScreen key="review" photos={photos} onRetake={() => beginCountdown(mode)} onAccept={handleAccept} onPrint={handlePrint} />
        )}
        {screen === 'print' && (
          <PrintPreviewScreen key="print" event={effectiveEvent} photos={photos} onBack={() => setScreen('review')} onDone={() => setScreen('share')} />
        )}
        {screen === 'survey' && (
          <SurveyScreen 
            key="survey" 
            event={effectiveEvent} 
            onDone={async (responses) => {
              if (sessionId) {
                await fetch(`/api/sessions/${sessionId}`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ surveyResponses: responses }),
                })
              }
              if (effectiveEvent.sharing.qrEnabled || effectiveEvent.sharing.emailEnabled) {
                setScreen('share')
              } else {
                setScreen('welcome')
              }
            }} 
          />
        )}
        {screen === 'share' && (
          <SharingScreen key="share" event={effectiveEvent} photos={photos} sessionId={sessionId} onDone={() => setScreen('welcome')} />
        )}
        {screen === 'thanks' && (
          <ThankYouScreen key="thanks" event={effectiveEvent} />
        )}
      </AnimatePresence>

      {/* Quick Settings Panel */}
      <AnimatePresence>
        {showQuickSettings && (
          <QuickSettings
            event={event}
            overrides={overrides}
            onChange={setOverrides}
            onClose={() => setShowQuickSettings(false)}
            onReset={() => setOverrides({})}
            onTriggerZigbeeLight={setZigbeeLight}
          />
        )}
      </AnimatePresence>

      {/* Debug Overlay */}
      {overrides.debugOverlayResolution && (
        <div className="absolute top-4 left-4 z-[200] bg-black/80 px-3 py-1.5 rounded-lg text-xs font-mono text-green-400 border border-green-500/20 shadow-lg">
          Config: {effectiveEvent?.camera.resolution.width}x{effectiveEvent?.camera.resolution.height} @ {effectiveEvent?.camera.frameRate}fps
        </div>
      )}

      {/* Maintenance Mode Overlay */}
      {overrides.maintenanceMode && (
        <div className="absolute inset-0 z-[300] flex flex-col items-center justify-center bg-black/50 backdrop-blur-xl text-white">
          <div className="text-center p-8 bg-card/20 backdrop-blur-md rounded-2xl border border-white/10 shadow-2xl max-w-lg">
            <h1 className="text-3xl font-bold mb-4">{overrides.maintenanceTitle || 'Under Maintenance'}</h1>
            <p className="text-lg opacity-90">
              {overrides.maintenanceText || 'Event currently in maintenance'}
            </p>
          </div>
        </div>
      )}

      {/* Permission Prompt Overlay */}
      <AnimatePresence>
        {isWaitingForPermission && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[400] bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center text-white"
          >
            <div className="absolute top-6 left-6 text-primary animate-bounce">
              <ArrowUpLeft className="h-16 w-16" />
            </div>
            <div className="text-center max-w-md p-6 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-md">
              <h2 className="text-2xl font-bold mb-3">Permissions Required</h2>
              <p className="text-white/70 text-sm leading-relaxed">
                Please click <strong className="text-white">Allow</strong> in the browser prompt at the top left to enable your camera and microphone.
              </p>
              <p className="text-white/50 text-xs mt-4">
                We need these to run the photoshoot and record session videos.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}



function LiveBeautyFilter({ event }: { event: EventConfig }) {
  const { enabled, strength } = event.effects.beautyFilter
  if (!enabled || strength <= 0) return null
  return (
    <div 
      className="absolute inset-0 pointer-events-none z-0" 
      style={{ 
        backdropFilter: `blur(${Math.round(strength * 3)}px)`, 
        opacity: strength * 0.35,
        backgroundColor: `rgba(255,255,255,${strength * 0.4})`
      }} 
    />
  )
}

function WelcomeScreen({ event, onStart, videoRef, attach, galleryQrData, overrides }: { event: EventConfig; onStart: () => void; videoRef: React.RefObject<HTMLVideoElement>; attach: () => void; galleryQrData: string | null; overrides: BoothOverrides }) {
  const screen = event.screens.welcome
  const showCamera = screen.useCameraBg !== false || !screen.backgroundImage
  const canvasW = screen.width || 1920
  const canvasH = screen.height || 1080
  const [scale, setScale] = useState(Math.min(window.innerWidth / canvasW, window.innerHeight / canvasH))

  useEffect(() => {
    const handleResize = () => setScale(Math.min(window.innerWidth / canvasW, window.innerHeight / canvasH))
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [canvasW, canvasH])

  useEffect(() => {
    attach()
  }, [attach])

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="relative h-full w-full overflow-hidden"
      style={{ backgroundColor: overrides.advancedBeautyFilter ? 'transparent' : (showCamera ? '#000' : screen.backgroundColor) }}
      onClick={onStart}>
      {screen.backgroundImage && !showCamera && <img src={screen.backgroundImage} className="absolute inset-0 h-full w-full object-cover" alt="" />}
      {/* Always render camera video on welcome — it's behind the elements overlay */}
      <video ref={videoRef} autoPlay playsInline muted className={`absolute inset-0 h-full w-full object-cover ${overrides.advancedBeautyFilter ? 'opacity-0' : ''}`} style={{ transform: event.camera.mirrorLiveView ? 'scaleX(-1)' : undefined, zIndex: 0 }} />
      
      <LiveBeautyFilter event={event} />


      {/* Canvas scaled and centered via absolute + translate — avoids flex layout issues with transform */}
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: canvasW, height: canvasH, transform: `translate(-50%, -50%) scale(${scale})`, transformOrigin: 'center center' }}>
        {screen.startButtonImage?.enabled && screen.startButtonImage.asset && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 100 }}>
            <img 
              src={screen.startButtonImage.asset} 
              style={{ 
                width: screen.startButtonImage.width ? `${screen.startButtonImage.width}px` : 'auto', 
                height: screen.startButtonImage.height ? `${screen.startButtonImage.height}px` : 'auto', 
                maxWidth: screen.startButtonImage.width ? undefined : '80%',
                maxHeight: screen.startButtonImage.height ? undefined : '80%',
                objectFit: 'contain',
                opacity: screen.startButtonImage.opacity ?? 1.0
              }} 
              alt="Start" 
            />
          </div>
        )}
        {screen.elements.map((el) => (
          <div key={el.id} className="absolute flex items-center justify-center overflow-hidden" style={{ left: el.x, top: el.y, width: el.width, height: el.height, transform: `rotate(${el.rotation}deg)`, zIndex: el.zIndex, color: el.color, fontSize: el.fontSize, fontFamily: el.fontFamily, textAlign: el.align || 'left', backgroundColor: el.backgroundColor, borderRadius: el.borderRadius, opacity: el.opacity }}>
            {el.type === 'text' && <span className="px-2">{el.content}</span>}
            {el.type === 'camera' && !showCamera && (
              <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" style={{ transform: event.camera.mirrorLiveView ? 'scaleX(-1)' : undefined }} />
            )}
            {el.type === 'camera' && showCamera && <span className="px-2 text-xs text-white/60">Camera</span>}
            {el.type === 'qr' && event?.sharing?.galleryEnabled && (
              <div className="flex flex-col h-full w-full items-center justify-center bg-white p-2 text-black rounded-lg">
                {galleryQrData ? (
                  <img src={galleryQrData} alt="Gallery QR" className="flex-1 object-contain min-h-0" />
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <QrCode className="h-10 w-10 text-black/40" />
                  </div>
                )}
                <span className="text-[10px] font-bold text-center mt-1 text-black leading-none">{el.content || 'Show Gallery'}</span>
              </div>
            )}
            {el.type === 'image' && el.src && <img src={el.src} alt="" className="h-full w-full object-cover" />}
            {el.type === 'image' && !el.src && <span className="px-2 text-xs text-white/60">Image</span>}
            {el.type === 'shape' && <div className="h-full w-full" style={{ backgroundColor: el.backgroundColor || '#3b82f6', borderRadius: el.borderRadius }} />}
          </div>
        ))}
      </div>
    </motion.div>
  )
}

function ModeSelectScreen({ event, onSelect }: { event: EventConfig; onSelect: (mode: CaptureMode) => void }) {
  const { t } = useI18n()
  const modes = event.capture.enabledModes

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex h-full w-full flex-col items-center justify-center gap-8 bg-black/80">
      <h2 className="text-4xl font-bold text-white">{t('booth.selectMode')}</h2>
      <div className="flex gap-6">
        {modes.map((m) => (
          <button key={m} onClick={() => onSelect(m)} className="flex h-40 w-40 flex-col items-center justify-center rounded-2xl border-2 border-white/20 bg-white/10 text-white backdrop-blur transition hover:bg-white/20">
            <span className="text-2xl font-semibold capitalize">{t(`capture.${m}`)}</span>
          </button>
        ))}
      </div>
    </motion.div>
  )
}

function CountdownScreen({ videoRef, event, countdown, currentPose, totalPoses, attach, allPoses, isPreCountdown, overrides, poseMode }: { videoRef: React.RefObject<HTMLVideoElement>; event: EventConfig; countdown: number; currentPose: number; totalPoses: number; attach: () => void; allPoses: string[]; isPreCountdown: boolean; overrides: BoothOverrides; poseMode: 'random' | 'ordered' }) {
  const { t } = useI18n()
  const [suggestions, setSuggestions] = useState<string[]>([])

  useEffect(() => {
    attach()
  }, [attach])

  useEffect(() => {
    if (allPoses.length > 0) {
      if (poseMode === 'ordered') {
        const start = (currentPose - 1) * 3
        let slice = allPoses.slice(start, start + 3)
        if (slice.length === 0) {
          slice = allPoses.slice(0, 3)
        }
        setSuggestions(slice)
      } else {
        const shuffled = [...allPoses].sort(() => 0.5 - Math.random())
        setSuggestions(shuffled.slice(0, 3))
      }
    }
  }, [allPoses, currentPose, poseMode])

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="relative h-full w-full overflow-hidden">
      <video ref={videoRef} autoPlay playsInline muted className={`absolute inset-0 h-full w-full object-cover ${overrides.advancedBeautyFilter ? 'opacity-0' : ''}`} style={{ transform: event.camera.mirrorLiveView ? 'scaleX(-1)' : undefined }} />
      <LiveBeautyFilter event={event} />
      
      {/* Dark overlay with subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/80" />

      <AnimatePresence mode="wait">
        {isPreCountdown ? (
          <motion.div 
            key="intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex flex-col items-center justify-center px-12"
          >
            <motion.h2 
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="mb-12 text-6xl font-black italic tracking-tighter text-white drop-shadow-2xl"
            >
              Pose suggestions!
            </motion.h2>

            <div className="flex gap-8 justify-center items-center w-full max-w-6xl">
              {suggestions.map((url, i) => (
                <motion.div
                  key={url}
                  initial={{ scale: 0.8, opacity: 0, y: 50 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15, type: "spring", damping: 15 }}
                  className="relative aspect-[3/4] w-72 overflow-hidden rounded-3xl border-4 border-white/20 bg-white/5 backdrop-blur-xl shadow-[0_0_50px_rgba(0,0,0,0.5)]"
                >
                  <img src={url} alt="" className="h-full w-full object-contain p-6" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </motion.div>
              ))}
            </div>

            {/* Intro Loading Bar */}
            <div className="mt-12 h-1.5 w-64 overflow-hidden rounded-full bg-white/10">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: event.capture.poseSuggestions?.showSecondsBefore ?? 2, ease: "linear" }}
                className="h-full bg-white/40"
              />
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-6 text-xl font-bold uppercase tracking-[0.5em] text-white/50"
            >
              Get Ready
            </motion.div>
          </motion.div>
        ) : (
          <motion.div 
            key="countdown"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex flex-col items-center justify-center"
          >
            {/* Progress header */}
            <div className="absolute top-12 flex flex-col items-center gap-2">
              <div className="text-sm font-bold uppercase tracking-[0.3em] text-white/40">{t('booth.pose')}</div>
              <div className="text-4xl font-black text-white italic">{currentPose} <span className="text-white/20 not-italic mx-1">/</span> {totalPoses}</div>
            </div>

            {/* Big countdown number */}
            <motion.div 
              key={countdown} 
              initial={{ scale: 1.5, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              className="text-[18rem] font-black text-transparent leading-none italic"
              style={{ WebkitTextStroke: '4px rgba(255,255,255,0.8)', filter: 'drop-shadow(0 0 30px rgba(255,255,255,0.2))' }}
            >
              {countdown}
            </motion.div>

            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="mt-8 text-2xl font-bold tracking-[0.4em] text-white/30 uppercase">
              {t('booth.getReady')}
            </motion.div>

            {/* Pose Suggestions (Small Luxury Style at bottom) */}
            {(event.capture.poseSuggestions?.enabled ?? true) && suggestions.length > 0 && (
              <motion.div 
                initial={{ y: 50, opacity: 0 }} 
                animate={{ y: 0, opacity: 1 }} 
                className="absolute bottom-12 w-full px-12"
              >
                <div className="mx-auto max-w-4xl">
                  <div className="mb-4 flex items-center gap-4">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-white/40 italic">Pose Suggestions</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  </div>
                  
                  <div className="flex justify-center gap-4">
                    {suggestions.map((url) => (
                      <motion.div 
                        key={url}
                        className="group relative h-32 w-28 overflow-hidden rounded-xl border border-white/10 bg-white/5 backdrop-blur-md shadow-2xl transition-all"
                      >
                        <img src={url} alt="" className="h-full w-full object-contain p-2" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Decorative corners */}
      <div className="absolute inset-8 border border-white/5 pointer-events-none">
        <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-white/20" />
        <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-white/20" />
        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-white/20" />
        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-white/20" />
      </div>
    </motion.div>
  )
}

function PhotoThumbnails({ photos, totalPoses, currentPose }: { photos: string[]; totalPoses: number; currentPose: number }) {
  return (
    <div className="absolute right-4 top-1/2 -translate-y-1/2 space-y-2 z-10">
      {Array.from({ length: totalPoses }).map((_, i) => (
        <div key={i} className={`h-16 w-16 rounded border-2 shadow-lg transition-all ${i < photos.length ? 'border-green-400' : i + 1 === currentPose ? 'border-white' : 'border-white/30'}`}>
          {i < photos.length && <img src={photos[i]} alt="" className="h-full w-full rounded object-cover" />}
        </div>
      ))}
    </div>
  )
}

function CaptureScreen({ videoRef, event, currentPose, totalPoses, photos, attach, overrides }: { videoRef: React.RefObject<HTMLVideoElement>; event: EventConfig; currentPose: number; totalPoses: number; photos: string[]; attach: () => void; overrides: BoothOverrides }) {
  useEffect(() => {
    attach()
  }, [attach])

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="relative h-full w-full bg-black">
      <video ref={videoRef} autoPlay playsInline muted className={`h-full w-full object-cover ${overrides.advancedBeautyFilter ? 'opacity-0' : ''}`} style={{ transform: event.camera.mirrorLiveView ? 'scaleX(-1)' : undefined }} />
      <LiveBeautyFilter event={event} />
      <PhotoThumbnails photos={photos} totalPoses={totalPoses} currentPose={currentPose} />
      <div className="absolute left-1/2 top-8 -translate-x-1/2 rounded-full bg-black/50 px-6 py-2 text-xl text-white">
        {currentPose} / {totalPoses}
      </div>
      {/* Flash overlay */}
      <motion.div initial={{ opacity: 1 }} animate={{ opacity: 0 }} transition={{ duration: 0.4 }} className="pointer-events-none absolute inset-0 bg-white" key={currentPose} />
    </motion.div>
  )
}

function IntermediateReviewScreen({ photo, photos, currentPose, totalPoses }: { photo: string | null; photos: string[]; currentPose: number; totalPoses: number }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="relative h-full w-full bg-black flex items-center justify-center">
      {photo ? (
        <img src={photo} alt="" className="max-h-full max-w-full object-contain shadow-2xl" />
      ) : (
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-white/20 border-t-white" />
          <span className="text-white/60 font-medium tracking-wide">Processing Photo...</span>
        </div>
      )}

      <PhotoThumbnails photos={photos} totalPoses={totalPoses} currentPose={currentPose} />

      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-8 py-3 text-2xl font-bold text-white backdrop-blur-md border border-white/20 shadow-2xl">
        Pose {currentPose} of {totalPoses} captured!
      </div>
    </motion.div>
  )
}

function ReviewScreen({ photos, onRetake, onAccept, onPrint }: { photos: string[]; onRetake: () => void; onAccept: () => void; onPrint: () => void }) {
  const { t } = useI18n()

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex h-full w-full flex-col items-center justify-center gap-6 bg-black/90">
      <div className="flex gap-4">
        {photos.map((p, i) => (
          <img key={i} src={p} alt="" className="h-64 rounded-lg border-2 border-white/20 object-contain" />
        ))}
      </div>
      <div className="flex gap-4">
        <button onClick={onRetake} className="rounded-lg bg-white/10 px-8 py-4 text-xl text-white hover:bg-white/20">{t('booth.retake')}</button>
        <button onClick={onPrint} className="rounded-lg bg-secondary px-8 py-4 text-xl text-secondary-foreground hover:bg-secondary/80">{t('booth.print')}</button>
        <button onClick={onAccept} className="rounded-lg bg-primary px-8 py-4 text-xl text-primary-foreground hover:bg-primary/90">{t('booth.accept')}</button>
      </div>
    </motion.div>
  )
}

function PrintPreviewScreen({ event, photos, onBack, onDone }: { event: EventConfig; photos: string[]; onBack: () => void; onDone: () => void }) {
  const [previewUrl, setPreviewUrl] = useState('')
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    let mounted = true
    generateLayoutImage(event, photos).then(url => {
      if (url && mounted) setPreviewUrl(url)
    })
    return () => { mounted = false }
  }, [event, photos])

  const handlePrint = () => {
    if (!previewUrl) return
    const w = window.open('', '_blank')
    if (!w) return
    w.document.write(`<html><body style="margin:0"><img src="${previewUrl}" style="max-width:100%"/><script>window.onload=()=>{window.print();window.close()}</script></body></html>`)
    w.document.close()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex h-full w-full flex-col items-center justify-center gap-6 bg-black/90">
      <canvas ref={canvasRef} className="hidden" />
      {previewUrl && <img src={previewUrl} alt="Print preview" className="max-h-[60vh] rounded-lg border-2 border-white/20" />}
      <div className="flex gap-4">
        <button onClick={onBack} className="rounded-lg bg-white/10 px-6 py-3 text-white hover:bg-white/20">Back</button>
        <button onClick={handlePrint} className="rounded-lg bg-primary px-6 py-3 text-primary-foreground hover:bg-primary/90">Print</button>
        <button onClick={onDone} className="rounded-lg bg-secondary px-6 py-3 text-secondary-foreground hover:bg-secondary/80">Done</button>
      </div>
    </motion.div>
  )
}

/** Seconds added per tap of the "need longer?" button. */
const SHARE_EXTEND_SECONDS = 15
/** Cap on extensions, so one guest can't camp the booth with a queue waiting. */
const SHARE_MAX_EXTENSIONS = 3

/**
 * Guest-facing text here is hardcoded in Dutch, matching PublicGallery and
 * PhotoViewer: the booth has no language switcher and every guest at these
 * events is a Dutch speaker. (The shared `locale` setting drives the admin
 * panel too, so switching that would translate the admin UI as well.)
 */
function SharingScreen({ event, photos, sessionId, onDone }: { event: EventConfig; photos: string[]; sessionId: string; onDone: () => void }) {
  const screen = event.screens.sharing
  const branding = event.branding
  const primaryColor = branding.primaryColor || '#3b82f6'
  const accentColor = branding.accentColor || primaryColor
  const [qrUrl, setQrUrl] = useState('')
  const timeout = Number(event.capture.finishScreenTimeout) || 20
  const [timeLeft, setTimeLeft] = useState(timeout)
  // The progress bar is measured against this rather than the fixed timeout, so
  // extending time doesn't push the bar past 100%.
  const [budget, setBudget] = useState(timeout)
  const [extensions, setExtensions] = useState(0)
  const [previewUrl, setPreviewUrl] = useState('')

  const [email, setEmail] = useState('')
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')

  const extendTime = () => {
    if (extensions >= SHARE_MAX_EXTENSIONS) return
    setExtensions((n) => n + 1)
    setTimeLeft((prev) => prev + SHARE_EXTEND_SECONDS)
    setBudget((prev) => prev + SHARE_EXTEND_SECONDS)
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 0.1) {
          clearInterval(timer)
          onDone()
          return 0
        }
        return prev - 0.1
      })
    }, 100)
    return () => clearInterval(timer)
  }, [onDone])

  useEffect(() => {
    if (event.sharing.qrEnabled) {
      import('@/lib/qr').then(({ generateQR }) => {
        let url = `${window.location.origin}/gallery?eventId=${event.id}&sessionId=${sessionId}`
        if (event.gallerySettings?.publicAccess === 'enabled' && event.gallerySettings?.publicToken) {
          url += `&token=${event.gallerySettings.publicToken}`
        }
        generateQR(url).then(setQrUrl)
      })
    }
  }, [event, sessionId])

  useEffect(() => {
    let mounted = true
    generateLayoutImage(event, photos).then(url => {
      if (url && mounted) setPreviewUrl(url)
    })
    return () => { mounted = false }
  }, [event, photos])

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !event.sharing.emailEnabled || !event.sharing.emailSmtp) return
    setEmailStatus('sending')
    try {
      const res = await fetch('/api/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          smtp: event.sharing.emailSmtp,
          to: email,
          subject: `Jouw foto's van ${event.name}`,
          html: `<p>Hoi!</p><p>Bedankt voor het gebruiken van de fotobooth bij <b>${event.name}</b>.</p><p>Je foto's zitten als bijlage bij deze e-mail.</p>`,
          attachments: [{ filename: 'photo.png', path: previewUrl }]
        }),
      })
      if (res.ok) {
        setEmailStatus('sent')
        setTimeout(() => setEmailStatus('idle'), 3000)
        setEmail('')
      } else {
        setEmailStatus('error')
      }
    } catch {
      setEmailStatus('error')
    }
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden cursor-pointer"
      style={{ backgroundColor: screen.backgroundColor || '#000000' }}
      onClick={onDone}
    >
      {screen.backgroundImage && (
        <img src={screen.backgroundImage} className="absolute inset-0 h-full w-full object-cover" alt="" />
      )}
      {/* Darkening overlay so text/QR stay legible over any background image */}
      <div className="absolute inset-0 bg-black/45" />

      <div className="relative z-10 flex h-full w-full flex-col md:flex-row items-center justify-center gap-8 max-w-[1800px] px-6 py-8">
        {/* Nice View / Layout Preview */}
        {previewUrl && (
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex-[1.6] flex justify-center items-center min-w-0 min-h-0 h-full">
            <img src={previewUrl} alt="Your Photo" className="max-h-[88vh] max-w-full w-auto h-auto object-contain rounded-lg shadow-2xl border border-white/10" />
          </motion.div>
        )}

        <div className="flex flex-col items-center gap-8 shrink-0 w-full max-w-sm">
          <div className="text-center space-y-2">
            <h2 className="text-5xl font-black text-white tracking-tight uppercase italic">Delen</h2>
            <p className="text-white/60 text-lg">Download direct je foto's</p>
          </div>

          <div className="grid w-full gap-8">
            {qrUrl && event.sharing.qrEnabled && (
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
                className="flex flex-col items-center gap-3"
              >
                <div
                  className="bg-white p-4 rounded-3xl"
                  style={{ boxShadow: `0 0 50px ${accentColor}40`, border: `3px solid ${primaryColor}` }}
                >
                  <img src={qrUrl} alt="QR" className="h-44 w-44" />
                </div>
                <span className="text-white/40 text-sm font-medium uppercase tracking-widest">Scan de QR-code</span>
              </motion.div>
            )}

            {event.sharing.emailEnabled && (
              <motion.form
                initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}
                onSubmit={handleSendEmail}
                onClick={(e) => e.stopPropagation()}
                className="flex flex-col gap-3"
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Vul je e-mailadres in"
                  className="w-full rounded-2xl bg-white/10 px-5 py-4 text-white text-lg placeholder:text-white/30 outline-none transition-all"
                  style={{ border: `1px solid ${primaryColor}55` }}
                />
                <button
                  type="submit"
                  disabled={emailStatus === 'sending' || !email}
                  className="w-full rounded-2xl py-4 text-white font-bold text-lg hover:opacity-90 transition-all disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  {emailStatus === 'sending' ? 'Versturen…' : emailStatus === 'sent' ? 'E-mail verzonden! 🎉' : 'Verstuur naar e-mail'}
                </button>
                {emailStatus === 'error' && <p className="text-red-400 text-xs text-center">Versturen mislukt. Probeer het later opnieuw.</p>}
              </motion.form>
            )}
          </div>

          <div className="flex flex-col items-center gap-4">
            <div
              className="bg-white/10 hover:bg-white/20 transition-colors px-8 py-3 rounded-full backdrop-blur-sm"
              style={{ border: `1px solid ${accentColor}66` }}
            >
              <span className="text-white font-bold text-lg uppercase tracking-widest">Tik om door te gaan</span>
            </div>
            <div className="text-white/40 text-xs font-mono uppercase tracking-tighter">
              Terug naar start in {Math.ceil(timeLeft)}s
            </div>

            {/* Grace period for anyone still fumbling with their camera app.
                stopPropagation is essential: without it this tap bubbles to the
                screen's onClick and ends the session instead of extending it. */}
            {extensions < SHARE_MAX_EXTENSIONS && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  extendTime()
                }}
                className="rounded-full bg-white/5 px-5 py-2 text-white/70 text-sm font-semibold active:bg-white/20 transition-colors"
                style={{ border: `1px solid ${accentColor}44` }}
              >
                Langer nodig? +{SHARE_EXTEND_SECONDS} sec
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Loading Bar / Timer */}
      <div className="absolute bottom-0 left-0 w-full h-3 bg-white/5 overflow-hidden z-10">
        <motion.div
          className="h-full"
          style={{ backgroundColor: primaryColor, boxShadow: `0 0 20px ${primaryColor}80` }}
          initial={{ width: "100%" }}
          animate={{ width: `${(timeLeft / budget) * 100}%` }}
          transition={{ ease: "linear", duration: 0.1 }}
        />
      </div>
    </motion.div>
  )
}

function SurveyScreen({ event, onDone }: { event: EventConfig; onDone: (responses: Record<string, string>) => void }) {
  const { t } = useI18n()
  const [responses, setResponses] = useState<Record<string, string>>({})
  const fields = event.survey || []

  const canContinue = fields.every(f => !f.required || (responses[f.id] && responses[f.id].trim() !== ''))

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex h-full w-full flex-col items-center justify-center gap-6 bg-black/90">
      <h2 className="text-3xl font-bold text-white">{t('survey.title')}</h2>
      <div className="w-full max-w-md space-y-4 px-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
        {fields.map((field) => (
          <div key={field.id} className="space-y-1">
            <label className="text-sm text-white/80">{field.label}{field.required && <span className="text-red-400"> *</span>}</label>
            {field.type === 'text' && <input type="text" value={responses[field.id] || ''} onChange={(e) => setResponses((prev) => ({ ...prev, [field.id]: e.target.value }))} className="w-full rounded border border-white/20 bg-white/10 px-3 py-2 text-white" />}
            {field.type === 'email' && <input type="email" value={responses[field.id] || ''} onChange={(e) => setResponses((prev) => ({ ...prev, [field.id]: e.target.value }))} className="w-full rounded border border-white/20 bg-white/10 px-3 py-2 text-white" />}
            {field.type === 'phone' && <input type="tel" value={responses[field.id] || ''} onChange={(e) => setResponses((prev) => ({ ...prev, [field.id]: e.target.value }))} className="w-full rounded border border-white/20 bg-white/10 px-3 py-2 text-white" />}
            {field.type === 'checkbox' && (
              <label className="flex items-center gap-2 text-white cursor-pointer">
                <input type="checkbox" checked={!!responses[field.id]} onChange={(e) => setResponses((prev) => ({ ...prev, [field.id]: e.target.checked ? 'yes' : '' }))} className="h-5 w-5" />
                Yes
              </label>
            )}
            {field.type === 'dropdown' && (
              <select value={responses[field.id] || ''} onChange={(e) => setResponses((prev) => ({ ...prev, [field.id]: e.target.value }))} className="w-full rounded border border-white/20 bg-white/10 px-3 py-2 text-white">
                <option value="">Select...</option>
                {field.options?.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            )}
            {field.type === 'gdpr' && (
              <label className="flex items-center gap-3 text-sm text-white cursor-pointer">
                <input type="checkbox" checked={!!responses[field.id]} onChange={(e) => setResponses((prev) => ({ ...prev, [field.id]: e.target.checked ? 'consented' : '' }))} className="h-5 w-5 shrink-0" />
                {field.label}
              </label>
            )}
          </div>
        ))}
      </div>
      <button 
        onClick={() => canContinue && onDone(responses)} 
        disabled={!canContinue}
        className="rounded-lg bg-primary px-12 py-4 text-xl font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
      >
        Continue
      </button>
    </motion.div>
  )
}

function ThankYouScreen({ event }: { event: EventConfig }) {
  const { t } = useI18n()
  const screen = event.screens.sharing

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex h-full w-full flex-col items-center justify-center gap-4" style={{ backgroundColor: screen.backgroundColor }}>
      {screen.backgroundImage && <img src={screen.backgroundImage} className="absolute inset-0 h-full w-full object-cover" alt="" />}
      <h2 className="z-10 text-5xl font-bold text-white">{t('booth.thankYou')}</h2>
    </motion.div>
  )
}
